import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';
import type { RootState, AppDispatch } from 'store';
import { changePanel, fetchRoofData } from 'store/slices/RoofDataSlice/roofDataSlice';
import { fetchIrradianceImages, setIrrandiance } from 'store/slices/IrradianceSlice';
import { isModeDefault } from '../../util';

export const irradianceListener = createListenerMiddleware();

/**
 * Fetch flux/irradiance images ONLY after a design load resolves, so `mode` is the freshly
 * fetched design's — never the persisted/rehydrated placeholder. Previously a render effect
 * fired get-flux-data against the rehydrated stale `mode` (DEFAULT) before design-by-uuid
 * updated it, producing a 400 for nearmap designs.
 *
 * All flux triggers route through these thunks/actions, so this covers every case:
 *   • initial load / fetch roof data → fetchRoofData.fulfilled
 *   • panel change                   → changePanel.fulfilled
 *   • imagery switch                 → changePanel.fulfilled (switchingSource: true)
 *   • mode switch                    → changePanel.fulfilled
 *   • facet coords edit              → changePanel.fulfilled (refetchIrradiance: true)
 *   • draw / create / delete facet   → changePanel.fulfilled (refetchIrradiance: true)
 *   • translate / pitch / rowSpacing / startingHeight edit (IN_HOUSE_SIMULATION only)
 *                                    → changePanel.fulfilled (refetchIrradiance: true)
 *   • design mode switch             → changePanel.fulfilled (refetchIrradiance: true)
 *   • DSM/shading completion         → refetchIrradiance (dispatched from dsmShadingListener)
 *
 * A `changePanel.fulfilled` only forces a refetch when the caller set `refetchIrradiance: true`
 * on the thunk arg (stale flux — geometry edit, mode/imagery switch); otherwise it is ignored
 * here and only fetches when nothing is cached via the normal cache guard. The explicit
 * `refetchIrradiance` action forces a fresh fetch even when images exist. All other guards
 * (SOLAR-only, default-mode-only, no double-fetch/retry-on-error) stay centralized here.
 */
irradianceListener.startListening({
	matcher: isAnyOf(fetchRoofData.fulfilled, changePanel.fulfilled),
	effect: (action, api) => {
		// changePanel only routes flux when the caller explicitly asked for it (stale flux)
		if (changePanel.fulfilled.match(action) && !action.meta.arg.refetchIrradiance) return;

		const state = api.getState() as RootState;
		const { mode, uuid, imagerySource, designType } = state.roofData.data;
		const { irradianceImages, showProgress, error } = state.Irradiance.data;

		const refetchIrradiance = (changePanel.fulfilled.match(action) && !!action.meta.arg.refetchIrradiance);

		// changePanel.pending set showProgress for a refetch — drop it if we end up not fetching
		const skipFetch = state.dsmShading.data.isRecomputing || designType !== 'SOLAR' || !isModeDefault(mode); // fetch for default modes only
		if (skipFetch) {
			if (refetchIrradiance && showProgress) api.dispatch(setIrrandiance({ showProgress: false }));
			return;
		}
		if (!refetchIrradiance && (error || showProgress)) return; // don't double-fetch / retry-on-error
		if (!refetchIrradiance && irradianceImages[imagerySource as keyof typeof irradianceImages]?.length) return;

		(api.dispatch as AppDispatch)(fetchIrradianceImages({ uuid, imagerySource }));
	},
});

/**
 * A 409 (Conflict) from get-flux-data is transient (e.g. flux still being generated), so it must
 * not be sticky like other errors. When the user turns irradiance on again after a 409, retry the
 * fetch. Same SOLAR / default-mode / not-recomputing guards as the load-time fetch above.
 */
irradianceListener.startListening({
	actionCreator: setIrrandiance,
	effect: (action, api) => {
		if (!action.payload.showIrradiance) return;

		const state = api.getState() as RootState;
		const { mode, uuid, imagerySource, designType } = state.roofData.data;
		const { showProgress, error } = state.Irradiance.data;

		if (error?.code !== 409 || showProgress) return;
		if (state.dsmShading.data.isRecomputing || designType !== 'SOLAR' || !isModeDefault(mode)) return;

		(api.dispatch as AppDispatch)(fetchIrradianceImages({ uuid, imagerySource }));
	},
});
